// Phase C — Public Carrier Bid page (no auth)
//
// Mounted at /carrier/bid/:token OUTSIDE ProtectedRoute. The token in the URL
// is the only gate. The page shows a carrier-redacted view of the deal
// (server applies view_tier='carrier' filter — no margin, no internal
// pricing, no other carriers' bids) and lets the carrier submit a single
// rate.
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Truck, Send, AlertCircle, CheckCircle2, Calendar, MapPin, Package } from "lucide-react";
import { usePublicCarrierLink, usePublicSubmitBid } from "../api/carrierBidder";

const mono = (size, color, weight, ls) => ({
  fontFamily: "'DM Mono', monospace",
  fontSize: size,
  color: color || "#f0f2f8",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const inputStyle = {
  background: "#0a0c14",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#f0f2f8",
  fontFamily: "'DM Mono', monospace",
  fontSize: 12,
  padding: "9px 10px",
  borderRadius: 6,
  width: "100%",
};

const KV = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3 py-1">
    <div style={mono(10, "#9aa1b3", 600, "0.6px")}>{k}</div>
    <div style={{ ...mono(12, "#f0f2f8", 500), textAlign: "right", maxWidth: 320 }}>{v ?? "—"}</div>
  </div>
);

export default function CarrierBidPage() {
  const { token } = useParams();
  const { data, isLoading, error } = usePublicCarrierLink(token);
  const submit = usePublicSubmitBid(token);
  const [rate, setRate] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [note, setNote] = useState("");
  const [estPickup, setEstPickup] = useState("");
  const [estDropoff, setEstDropoff] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [err, setErr] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    const rateNum = Number(rate);
    if (!rateNum || rateNum <= 0) {
      setErr("Please enter your rate (greater than 0).");
      return;
    }
    try {
      const r = await submit.mutateAsync({
        rate: rateNum,
        currency: (currency || "EUR").toUpperCase(),
        note: note || null,
        estimated_pickup_at: estPickup || null,
        estimated_dropoff_at: estDropoff || null,
      });
      setSubmitted(r);
    } catch (e2) {
      const det = e2?.response?.data?.detail;
      const code = det?.code;
      if (code === "already_submitted") {
        setErr("This link has already been used to submit a bid. Please contact the forwarder if you need to update.");
      } else if (code === "link_expired") {
        setErr("This link has expired. Please ask the forwarder for a fresh one.");
      } else {
        setErr(typeof det === "string" ? det : (det?.message || "Submission failed."));
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#08090d" }}>
        <div style={mono(11, "#9aa1b3", 600, "1.5px")}>LOADING…</div>
      </div>
    );
  }
  if (error || !data) {
    const det = error?.response?.data?.detail || {};
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#08090d", padding: 24 }}>
        <div
          className="rounded-md p-6 max-w-md text-center"
          style={{ background: "#0d0f18", border: "1px solid rgba(226,75,74,0.45)" }}
          data-testid="carrier-bid-invalid"
        >
          <AlertCircle size={28} style={{ color: "#ff8a8a", margin: "0 auto 10px" }} />
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 18, color: "#ff8a8a", marginBottom: 8 }}>Link unavailable</h1>
          <div style={mono(11, "#cfd5e3", 400, "0.4px")}>
            {det.code === "link_expired"
              ? "This link has expired. Please request a new one from the forwarder."
              : "This link is invalid or has been revoked."}
          </div>
        </div>
      </div>
    );
  }

  const { link, deal } = data;
  const pickup = (deal.pickups || [])[0] || {};
  const dropoff = (deal.dropoffs || [])[0] || {};

  if (submitted) {
    return (
      <div style={{ minHeight: "100vh", background: "#08090d", padding: 24 }}>
        <div
          className="max-w-2xl mx-auto rounded-md p-6 text-center"
          style={{ background: "#0d0f18", border: "1px solid rgba(78,208,132,0.45)" }}
          data-testid="carrier-bid-success"
        >
          <CheckCircle2 size={42} style={{ color: "#4ed084", margin: "0 auto 12px" }} />
          <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 22, color: "#4ed084", marginBottom: 6 }}>Bid received</h1>
          <div style={mono(12, "#cfd5e3", 400)}>
            Thanks {link.carrier_name}. Your rate of <strong>{submitted.bid.rate} {submitted.bid.currency}</strong> has been delivered to the forwarder. You can close this window.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#08090d", padding: 24 }} data-testid="carrier-bid-page">
      <div className="max-w-3xl mx-auto">
        <header
          className="rounded-md p-4 mb-4 flex items-center gap-3"
          style={{ background: "linear-gradient(90deg, #0d0f18 0%, #1a1322 100%)", border: "1px solid rgba(243,177,74,0.30)" }}
          data-testid="carrier-bid-header"
        >
          <div
            className="rounded-md p-2"
            style={{ background: "rgba(243,177,74,0.15)", border: "1px solid rgba(243,177,74,0.40)" }}
          >
            <Truck size={18} color="#f3b14a" />
          </div>
          <div className="flex-1">
            <div style={{ ...mono(9, "#9aa1b3", 700, "1.4px"), textTransform: "uppercase" }}>Rate request</div>
            <h1 style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 20, color: "#f0f2f8" }}>
              {deal.deal_name || deal.id}
            </h1>
            <div style={mono(10, "#cfd5e3")} data-testid="carrier-bid-recipient">
              For: <strong>{link.carrier_name}</strong> · Expires {(link.expires_at || "").slice(0, 10)}
            </div>
          </div>
        </header>

        <section
          className="rounded-md p-4 mb-4 space-y-3"
          style={{ background: "#0d0f18", border: "1px solid rgba(255,255,255,0.10)" }}
          data-testid="carrier-bid-deal"
        >
          <div style={{ ...mono(10, "#cfd5e3", 700, "1.2px"), textTransform: "uppercase", marginBottom: 4 }}>
            Movement
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-1 mb-1" style={mono(9, "#9aa1b3", 700, "1px")}>
                <MapPin size={11} /> PICKUP
              </div>
              <KV k="City" v={pickup.city} />
              <KV k="Country" v={pickup.country} />
              <KV k="Postal" v={pickup.postal_code} />
              <KV k="Window from" v={(pickup.window_start_at || "").slice(0, 16).replace("T", " ")} />
              <KV k="Window to" v={(pickup.window_end_at || "").slice(0, 16).replace("T", " ")} />
            </div>
            <div>
              <div className="flex items-center gap-1 mb-1" style={mono(9, "#9aa1b3", 700, "1px")}>
                <MapPin size={11} /> DROPOFF
              </div>
              <KV k="City" v={dropoff.city} />
              <KV k="Country" v={dropoff.country} />
              <KV k="Postal" v={dropoff.postal_code} />
              <KV k="Window from" v={(dropoff.window_start_at || "").slice(0, 16).replace("T", " ")} />
              <KV k="Window to" v={(dropoff.window_end_at || "").slice(0, 16).replace("T", " ")} />
            </div>
          </div>
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: 10 }}>
            <div className="flex items-center gap-1 mb-1" style={mono(9, "#9aa1b3", 700, "1px")}>
              <Package size={11} /> CARGO
            </div>
            <KV k="Goods" v={deal.aru_megnevezese} />
            <KV k="Vehicle type" v={deal.autotipus} />
            <KV k="Weight (kg)" v={deal.suly_kg} />
            <KV k="LDM" v={deal.rakmeter} />
            <KV k="Volume (m³)" v={deal.kobmeter} />
            <KV k="Deadline" v={deal.leadasi_hatarido} />
          </div>
        </section>

        <form
          onSubmit={onSubmit}
          className="rounded-md p-4 space-y-3"
          style={{ background: "#0d0f18", border: "1px solid rgba(243,177,74,0.30)" }}
          data-testid="carrier-bid-form"
        >
          <div style={{ ...mono(10, "#f3b14a", 700, "1.2px"), textTransform: "uppercase", marginBottom: 4 }}>
            Submit your rate
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
            <label className="block">
              <div style={mono(9, "#9aa1b3", 700, "0.8px")}>YOUR RATE</div>
              <input
                type="number"
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                style={inputStyle}
                placeholder="1450"
                data-testid="carrier-bid-rate"
                autoFocus
              />
            </label>
            <label className="block">
              <div style={mono(9, "#9aa1b3", 700, "0.8px")}>CURRENCY</div>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                style={inputStyle}
                data-testid="carrier-bid-currency"
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="HUF">HUF</option>
                <option value="GBP">GBP</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <div style={mono(9, "#9aa1b3", 700, "0.8px")}><Calendar size={9} className="inline mr-1" />ESTIMATED PICKUP</div>
              <input
                type="datetime-local"
                value={estPickup}
                onChange={(e) => setEstPickup(e.target.value)}
                style={inputStyle}
                data-testid="carrier-bid-est-pickup"
              />
            </label>
            <label className="block">
              <div style={mono(9, "#9aa1b3", 700, "0.8px")}><Calendar size={9} className="inline mr-1" />ESTIMATED DROPOFF</div>
              <input
                type="datetime-local"
                value={estDropoff}
                onChange={(e) => setEstDropoff(e.target.value)}
                style={inputStyle}
                data-testid="carrier-bid-est-dropoff"
              />
            </label>
          </div>
          <label className="block">
            <div style={mono(9, "#9aa1b3", 700, "0.8px")}>NOTE (optional)</div>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{ ...inputStyle, fontFamily: "'DM Mono', monospace" }}
              placeholder="Equipment type, restrictions, ADR class, etc."
              data-testid="carrier-bid-note"
            />
          </label>
          {err && (
            <div
              className="rounded px-3 py-2 inline-flex items-start gap-2"
              style={{ background: "rgba(226,75,74,0.10)", border: "1px solid rgba(226,75,74,0.40)", color: "#ff8a8a", ...mono(11), width: "100%" }}
              data-testid="carrier-bid-error"
            >
              <AlertCircle size={13} className="mt-0.5" />
              <div>{err}</div>
            </div>
          )}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submit.isPending}
              className="px-4 py-2 rounded inline-flex items-center gap-2"
              style={{
                background: "#f3b14a",
                color: "#08090d",
                border: "none",
                ...mono(12, "#08090d", 700, "0.8px"),
                textTransform: "uppercase",
                opacity: submit.isPending ? 0.6 : 1,
                cursor: submit.isPending ? "not-allowed" : "pointer",
              }}
              data-testid="carrier-bid-submit"
            >
              <Send size={13} /> {submit.isPending ? "Sending…" : "Submit rate"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
